import { useCallback } from "react";

import { Button, Text } from "@fluentui/react-components";
import { PersonInfoRegular } from "@fluentui/react-icons";
import { useAtomValue, useSetAtom } from "jotai";
import { ROUTES } from "library/routes";
import { EProfileTab } from "library/types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { selectedClientAtom } from "store";
import { selectedProfileTabAtom } from "store/UIProfilePage";
import styled from "styled-components";

import { MoreDotsIcon } from "components/AppLayout/icons/MoreDotsIcon";
import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import useEmployee from "hooks/useEmployee";
import { useHasGivenConsent } from "hooks/useHasGivenConsent";

export function ProfileHeader() {
  const theme = useAppTheme();
  const { t } = useTranslation();
  const selectedClient = useAtomValue(selectedClientAtom);
  const { employeeData } = useEmployee();
  const navigate = useNavigate();

  const setSelectedTab = useSetAtom(selectedProfileTabAtom);
  const hasGivenConsent = useHasGivenConsent();

  const onSelectTab = useCallback((tab: EProfileTab) => {
    setSelectedTab(tab);
  }, []);

  return (
    <Container>
      <Flex dir="column" align="center" $justify="center">
        <ImageWrap>
          {employeeData?.backgroundPicturePath ? (
            <img src={employeeData?.backgroundPicturePath} alt="user-profile" />
          ) : (
            <NoImagePlaceholder>{`${employeeData?.firstName?.charAt(0)}${employeeData?.lastName?.charAt(0)}`}</NoImagePlaceholder>
          )}
        </ImageWrap>
        <div>
          <Text
            style={{
              fontSize: "22px",
            }}
            weight="semibold"
          >{`${employeeData?.firstName} ${employeeData?.lastName} `}</Text>
          <Text size={200} block>{`${selectedClient?.clientName}`}</Text>
        </div>
      </Flex>
      <ButtonsWrap>
        <Button
          data-help="nav-profile-details"
          disabled={!hasGivenConsent}
          appearance="outline"
          style={{
            fontWeight: 400,
          }}
          onClick={() => {
            onSelectTab(EProfileTab.info);
            navigate(ROUTES.PROFILE);
          }}
          icon={<PersonInfoRegular />}
        >
          {t("hbh.userDetails.label")}
        </Button>
        <Button
          style={{
            fontWeight: 400,
          }}
          disabled={!hasGivenConsent}
          appearance="outline"
          icon={<MoreDotsIcon />}
          onClick={() => {
            onSelectTab(EProfileTab.documents);
            navigate(ROUTES.PROFILE);
          }}
        >
          {t("hbh.more.label")}
        </Button>
      </ButtonsWrap>
    </Container>
  );
}

const Container = styled.div`
  padding: 20px;
  position: relative;
  margin-top: 20px;
  display: flex;
  gap: 10px;
  flex-direction: column;
`;

const ImageWrap = styled.div`
  display: flex;
  width: 100%;
  border-radius: 50%;
  justify-content: center;
  img {
    width: 70px;
    height: 70px;
    border: 2px solid ${({ theme }) => theme.palette.white};
    border-radius: 50%;
  }
`;

const ButtonsWrap = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
`;
const NoImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
  justify-content: center;
  font-weight: 600;

  width: 70px;
  height: 70px;
  border: 2px solid ${({ theme }) => theme.palette.white};
  background: ${({ theme }) => theme.palette.neutralLighterAlt};
  border-radius: 50%;
`;
